import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import type { CreatedFilter } from '~/components/tasks/CreatedDateFilter';
import {
  inCreatedRange,
  isClosedStatus,
  isFollowUpOverdue,
  isoDate,
} from '~/components/tasks/taskStyle';
import { dateToDateString } from '~/components/utils/dateUtils';
import type { TaskSummary } from '~/data/generated/model';

export type FollowUpFilter = 'overdue' | 'week';

export type TaskFilters = {
  propertyId: string | null;
  ownerId: string | null;
  followUp: FollowUpFilter | null;
  query: string;
};

const PARAM = {
  propertyId: 'taskProperty',
  ownerId: 'taskOwner',
  followUp: 'taskFollowUp',
  query: 'taskSearch',
} as const;

export const NO_FILTERS: TaskFilters = {
  propertyId: null,
  ownerId: null,
  followUp: null,
  query: '',
};

function readFollowUp(value: string | null): FollowUpFilter | null {
  return value === 'overdue' || value === 'week' ? value : null;
}

export function readTaskFilters(params: URLSearchParams): TaskFilters {
  return {
    propertyId: params.get(PARAM.propertyId),
    ownerId: params.get(PARAM.ownerId),
    followUp: readFollowUp(params.get(PARAM.followUp)),
    query: params.get(PARAM.query) ?? '',
  };
}

export function useTaskFilters() {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => readTaskFilters(params), [params]);
  const setFilter = useCallback(
    <K extends keyof TaskFilters>(key: K, value: TaskFilters[K]) =>
      setParams(
        (prev) => {
          const encoded = value || null;
          if (encoded === null) prev.delete(PARAM[key]);
          else prev.set(PARAM[key], String(encoded));
          return prev;
        },
        { replace: true },
      ),
    [setParams],
  );
  const clearFilters = useCallback(
    () =>
      setParams(
        (prev) => {
          Object.values(PARAM).forEach((param) => prev.delete(param));
          return prev;
        },
        { replace: true },
      ),
    [setParams],
  );
  return { filters, setFilter, clearFilters };
}

export function hasActiveFilters(filters: TaskFilters): boolean {
  return (
    Boolean(filters.propertyId || filters.ownerId || filters.followUp) ||
    Boolean(filters.query.trim())
  );
}

function dueThisWeek(task: TaskSummary, today: Date): boolean {
  if (task.followUpDate === null) return false;
  const end = new Date(today);
  end.setDate(end.getDate() + 7);
  return task.followUpDate >= isoDate(today) && task.followUpDate <= isoDate(end);
}

export function matchesTaskFilters(
  task: TaskSummary,
  filters: TaskFilters,
  today: Date = new Date(),
): boolean {
  if (filters.propertyId && task.propertyId !== filters.propertyId) return false;
  if (filters.ownerId && task.assignee?.id !== filters.ownerId) return false;
  if (filters.followUp === 'overdue' && !isFollowUpOverdue(task, today)) return false;
  if (filters.followUp === 'week' && !dueThisWeek(task, today)) return false;
  const query = filters.query.trim().toLowerCase();
  if (!query) return true;
  const haystack = [
    task.title,
    task.displayId,
    task.assignee?.name ?? '',
    task.propertyName,
    ...task.cars.map((car) => car.name),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

/** Open tasks always show; the period only narrows closed ones. */
export function shownInPeriod(task: TaskSummary, period: { start: Date; end: Date }): boolean {
  if (!isClosedStatus(task.status)) return true;
  if (period.start.getTime() === 0 && period.end.getTime() === 0) return true;
  const created = isoDate(new Date(task.createdAt));
  return created >= dateToDateString(period.start) && created <= dateToDateString(period.end);
}

export function createdInFilter(task: TaskSummary, created: CreatedFilter): boolean {
  return inCreatedRange(task.createdAt, created.range, created.from, created.to);
}

export type TaskSort = 'created' | 'followUp' | 'updated';

export const TASK_SORTS: { value: TaskSort; label: string }[] = [
  { value: 'created', label: 'Newest first' },
  { value: 'followUp', label: 'Follow-up date' },
  { value: 'updated', label: 'Last updated' },
];

const lastTouched = (task: TaskSummary) => task.updatedAt ?? task.createdAt;

/** Follow-up sorts soonest first with undated tasks last; the others sort newest first. */
export function sortTasks(tasks: TaskSummary[], sort: TaskSort): TaskSummary[] {
  const newestFirst = (a: string, b: string) => (a < b ? 1 : a > b ? -1 : 0);
  return [...tasks].sort((a, b) => {
    if (sort === 'followUp') {
      if (a.followUpDate !== b.followUpDate) {
        if (a.followUpDate === null) return 1;
        if (b.followUpDate === null) return -1;
        return a.followUpDate < b.followUpDate ? -1 : 1;
      }
    } else if (sort === 'updated') {
      const order = newestFirst(lastTouched(a), lastTouched(b));
      if (order !== 0) return order;
    }
    return newestFirst(a.createdAt, b.createdAt);
  });
}
